/**
* Adapted from https://github.com/onurzorluer/exif-auto-rotate/blob/master/src/index.js
* @author Onur Zorluer:
* */
export const rotateExif = async (file: File): Promise<string> => new Promise((resolve, reject) => {
  const rotateImage = (image: HTMLImageElement, srcOrientation: number) => {
    const { width, height } = image;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // set proper canvas dimensions before transform & export
    canvas.width = width;
    canvas.height = height;

    // transform context before drawing image
    switch (srcOrientation) {
      case 2: {
        ctx.transform(-1, 0, 0, 1, width, 0);
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
        break;
      }
      case 3: {
        ctx.transform(-1, 0, 0, -1, width, height);
        ctx.translate(width, height);
        ctx.rotate(Math.PI);
        break;
      }
      case 4: {
        ctx.transform(1, 0, 0, -1, 0, height);
        ctx.translate(0, height);
        ctx.scale(1, -1);
        break;
      }
      case 5: {
        ctx.transform(0, 1, 1, 0, 0, 0);
        ctx.rotate(-0.5 * Math.PI);
        ctx.scale(-1, 1);
        break;
      }
      case 6: {
        ctx.transform(0, 1, -1, 0, width, 0);
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-width, 0);
        break;
      }
      case 7: {
        ctx.transform(0, -1, -1, 0, width, height);
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-width, height);
        ctx.scale(1, -1);
        break;
      }
      case 8: {
        ctx.transform(0, -1, 1, 0, 0, height);
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(0, -height);
        break;
      }
      default:
        break;
    }

    // draw image
    ctx.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL(`image/${'JPEG'}`, 100);
  };

  const getOrientation = (callback: (result: number) => void) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const view = new DataView(event.target.result as ArrayBufferLike);

      if (view.getUint16(0, false) !== 0xFFD8) {
        return callback(-2);
      }

      const length = view.byteLength;
      let offset = 2;

      while (offset < length) {
        const marker = view.getUint16(offset, false);
        offset += 2;

        if (marker === 0xFFE1) {
          offset += 2;

          if (view.getUint32(offset, false) !== 0x45786966) {
            return callback(-1);
          }

          let little = view.getUint16(offset += 6, false) === 0x4949;
          offset += view.getUint32(offset + 4, little);

          let tags = view.getUint16(offset, little);
          offset += 2;

          for (let i = 0; i < tags; i += 1) {
            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
              return callback(view.getUint16(offset + (i * 12) + 8, little));
            }
          }
        } else if ((marker & 0xFF00) !== 0xFF00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }

      return callback(-1);
    };

    reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
  };

  const rotatorFunction = (imageOrientation: number, image: HTMLImageElement) => {
    if (imageOrientation === -2) {
      reject(new Error('Image is NOT JPEG'));
    } else if (imageOrientation === -1) {
      reject(new Error('Image is NOT have a exif code'));
    }

    resolve(rotateImage(image, imageOrientation));
  };

  if (file) {
    if (file.type && !file.type.includes('image')) {
      reject(new Error('File Is NOT Image'));
    } else {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        const image = new Image();

        image.src = reader.result as string;
        image.onload = () => {
          getOrientation((orientation) => {
            rotatorFunction(orientation, image);
          });
        };
      };

      reader.onerror = (error) => {
        reject(error);
      };
    }
  } else {
    reject(new Error('File Not Found!'));
  }
});
